import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Wallet, TrendingUp, ShoppingCart, CheckCircle2, Clock,
  Copy, Printer, Percent, Phone as PhoneIcon, MessageCircle, RefreshCw, Zap, Ticket, Search as SearchIcon,
  QrCode, List
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import VoucherUsageCheckModal from '../components/VoucherUsageCheckModal';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const fmtDur = (period, unit) => {
  if (!period) return '-';
  const map = { hours: 'Jam', days: 'Hari', weeks: 'Minggu', months: 'Bulan' };
  return `${period} ${map[unit] || unit || ''}`;
};

export default function AgentDashboard() {
  const { token } = useAuth();
  const [usageOpen, setUsageOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pkgModal, setPkgModal] = useState(null); // selected package
  const [pkgVouchers, setPkgVouchers] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'qr' 

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/voucher-agents/me/dashboard`, auth);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchDashboard(); /* eslint-disable-next-line */ }, [token]);

  const openPackage = async (pkg) => {
    setPkgModal(pkg);
    setPkgLoading(true);
    setPkgVouchers([]);
    setViewMode('list');
    try {
      const res = await axios.get(
        `${API_URL}/api/voucher-agents/me/available-vouchers?package_id=${pkg.package_id}`, auth
      );
      setPkgVouchers(res.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memuat voucher');
    } finally {
      setPkgLoading(false);
    }
  };

  const copyCodes = () => {
    if (!pkgVouchers.length) return;
    const text = pkgVouchers.map(v => `${v.code}\t${v.password || v.code}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success(`${pkgVouchers.length} kode voucher tersalin ke clipboard`);
  };

  const printCodes = () => {
    if (!pkgVouchers.length) return;
    const w = window.open('', '_blank', 'width=800,height=600');
    const rows = pkgVouchers.map(v => `
      <tr>
        <td>${v.code}</td>
        <td>${v.password || v.code}</td>
        <td>${v.package_name}</td>
        <td style="text-align:right">${fmtIDR(v.price)}</td>
        <td>${fmtDur(v.validity_days, v.validity_unit)}</td>
      </tr>
    `).join('');
    w.document.write(`
      <html><head><title>Voucher ${pkgModal?.package_name} - ${data?.agent?.name}</title>
      <style>
        body{font-family:Arial;padding:20px;color:#111}
        h1{margin:0 0 4px 0;font-size:18px}
        .info{color:#666;font-size:12px;margin-bottom:12px}
        table{border-collapse:collapse;width:100%;font-size:12px}
        th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
        th{background:#f5f5f5}
        @media print{@page{margin:1cm}}
      </style></head><body>
      <h1>Daftar Voucher Tersedia — ${pkgModal?.package_name}</h1>
      <div class="info">Agen: ${data?.agent?.name} • Total: ${pkgVouchers.length} voucher • Dicetak: ${new Date().toLocaleString('id-ID')}</div>
      <table><thead><tr><th>Kode</th><th>Password</th><th>Paket</th><th>Harga</th><th>Masa Aktif</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.print();</script>
      </body></html>
    `);
    w.document.close();
  };

  // Print QR cards (printable 4 cards per row, A4)
  const printQRCards = () => {
    if (!pkgVouchers.length) return;
    const w = window.open('', '_blank', 'width=1000,height=800');
    // Encode QR content: multi-line text scannable by phone camera
    const qrCards = pkgVouchers.map(v => {
      const qrText = window.location.origin + '/hs/redeem?u=' + encodeURIComponent(v.code) + '&p=' + encodeURIComponent(v.password || v.code);
      return `
      <div class="card">
        <div class="brand">${data?.agent?.branch_name || 'HOTSPOT'}</div>
        <div class="pkg">${v.package_name}</div>
        <div class="qr-holder" data-qr="${encodeURIComponent(qrText)}"></div>
        <div class="code">${v.code}</div>
        <div class="pw">${v.password || v.code}</div>
        <div class="meta">${fmtDur(v.validity_days, v.validity_unit)} ${v.speed_down ? '· ' + v.speed_down : ''}</div>
        <div class="price">${fmtIDR(v.price)}</div>
      </div>`;
    }).join('');
    w.document.write(`
      <html><head><title>QR Voucher ${pkgModal?.package_name}</title>
      <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
      <style>
        body{font-family:'Segoe UI',Arial,sans-serif;padding:8mm;color:#111;background:#fff;margin:0}
        .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:6mm}
        .card{border:1.5px dashed #666;border-radius:4mm;padding:3mm;text-align:center;page-break-inside:avoid;background:#fff}
        .brand{font-size:9pt;font-weight:700;color:#f59e0b;text-transform:uppercase;letter-spacing:0.5px}
        .pkg{font-size:11pt;font-weight:800;margin:1mm 0 2mm 0;color:#111}
        .qr-holder{display:flex;justify-content:center;margin:2mm 0}
        .code{font-family:'Courier New',monospace;font-size:12pt;font-weight:700;background:#fef3c7;padding:1mm 2mm;border-radius:1mm;margin-top:1mm;letter-spacing:1px}
        .pw{font-family:'Courier New',monospace;font-size:10pt;color:#6b7280;margin-top:1mm}
        .meta{font-size:8pt;color:#6b7280;margin-top:1mm}
        .price{font-size:10pt;font-weight:700;color:#059669;margin-top:2mm}
        h1{margin:0 0 3mm 0;font-size:14pt}
        .info{color:#666;font-size:9pt;margin-bottom:5mm;border-bottom:1px solid #ddd;padding-bottom:2mm}
        @media print{@page{margin:8mm;size:A4}body{padding:0}}
      </style></head><body>
      <h1>Voucher ${pkgModal?.package_name} — Scan &amp; Login</h1>
      <div class="info">Agen: ${data?.agent?.name} · Cabang: ${data?.agent?.branch_name || '-'} · ${pkgVouchers.length} kartu · Dicetak: ${new Date().toLocaleString('id-ID')}</div>
      <div class="grid">${qrCards}</div>
      <script>
        window.addEventListener('load', function() {
          var holders = document.querySelectorAll('.qr-holder');
          holders.forEach(function(el) {
            var text = decodeURIComponent(el.getAttribute('data-qr'));
            var canvas = document.createElement('canvas');
            QRCode.toCanvas(canvas, text, { width: 100, margin: 1 }, function(err){});
            el.appendChild(canvas);
          });
          setTimeout(function(){ window.print(); }, 800);
        });
      </script>
      </body></html>
    `);
    w.document.close();
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-slate-500">
        Data tidak ditemukan. <Button size="sm" onClick={fetchDashboard} className="ml-2">Reload</Button>
      </div>
    );
  }

  const { agent, stats, packages, recent_sold } = data;
  const sellRate = stats.total > 0 ? Math.round((stats.sold / stats.total) * 100) : 0;

  return (
    <div className="p-3 sm:p-6 space-y-4" data-testid="agent-dashboard">
      {/* Welcome Header */}
      <Card className="shadow-md bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 border-0 text-white overflow-hidden relative">
        <CardContent className="p-5 relative">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-white/80 text-xs font-medium">Selamat datang,</p>
              <h1 className="text-2xl sm:text-3xl font-bold">{agent.name}</h1>
              <p className="text-white/90 text-xs mt-1">
                Cabang: <span className="font-semibold">{agent.branch_name || '-'}</span>
                {agent.whatsapp && <> • <MessageCircle className="w-3 h-3 inline" /> {agent.whatsapp}</>}
              </p>
            </div>
            <div className="text-left sm:text-right bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <p className="text-white/80 text-xs">Komisi per Voucher</p>
              <p className="text-2xl font-bold flex items-center gap-1 sm:justify-end">
                {agent.commission_type === 'percent' ? (
                  <><Percent className="w-5 h-5" /> {agent.commission_value}%</>
                ) : (
                  <><Wallet className="w-5 h-5" /> {fmtIDR(agent.commission_value)}</>
                )}
              </p>
              <p className="text-white/70 text-[10px] mt-0.5">
                {agent.commission_type === 'percent' ? 'dari harga voucher' : 'flat per voucher'}
              </p>
            </div>
          </div>
          <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none">
            <Ticket className="w-40 h-40" />
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => setUsageOpen(true)}
          variant="outline"
          className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white shadow-sm"
          data-testid="agent-usage-check-btn"
        >
          <SearchIcon className="w-4 h-4 mr-1" />
          Cek Pemakaian Voucher
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Package}
          label="Total Voucher"
          value={stats.total || 0}
          color="slate"
          testid="stat-total"
        />
        <StatCard
          icon={CheckCircle2}
          label="Siap Dijual"
          value={stats.available || 0}
          subtext={stats.available > 0 ? '⚡ Ayo jual!' : 'Habis'}
          color="emerald"
          highlight
          testid="stat-available"
        />
        <StatCard
          icon={ShoppingCart}
          label="Sudah Terjual"
          value={stats.sold || 0}
          subtext={`${sellRate}% terjual`}
          color="blue"
          testid="stat-sold"
        />
        <StatCard
          icon={Wallet}
          label="Komisi Diterima"
          value={fmtIDR(stats.commission_earned || 0)}
          subtext={stats.commission_potential > 0 ? `Potensi: ${fmtIDR(stats.commission_potential)}` : null}
          color="orange"
          isText
          testid="stat-commission"
        />
      </div>

      {/* Progress bar overall */}
      {stats.total > 0 && (
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2 text-xs">
              <span className="text-slate-600 dark:text-slate-300 font-medium">Progress Penjualan</span>
              <span className="text-slate-900 dark:text-white font-bold">{sellRate}%</span>
            </div>
            <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                   style={{ width: `${sellRate}%` }} />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-slate-500">
              <span>{stats.sold || 0} terjual</span>
              <span>{stats.available || 0} tersisa</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Packages Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-500" />
            Paket yang Tersedia
          </h2>
          <Button size="sm" variant="outline" onClick={fetchDashboard} className="h-8" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        {packages.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="text-center text-slate-500 py-10">
              Belum ada voucher yang di-assign ke Anda. Hubungi Kepala Cabang.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packages.map((p) => (
              <Card
                key={p.package_id}
                className={`shadow-sm border transition-all cursor-pointer hover:shadow-md hover:scale-[1.01] ${
                  p.available > 0 ? 'border-emerald-200 hover:border-emerald-400 dark:border-emerald-900' : 'border-slate-200 dark:border-slate-800 opacity-70'
                }`}
                onClick={() => openPackage(p)}
                data-testid={`pkg-card-${p.package_id}`}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white truncate">{p.package_name}</p>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDur(p.validity_days, p.validity_unit)}</span>
                        {p.speed_down && <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {p.speed_down}</span>}
                      </div>
                    </div>
                    <span className="text-sm font-bold text-amber-600 shrink-0">{fmtIDR(p.price)}</span>
                  </div>

                  {/* Available count - HERO */}
                  <div className={`rounded-lg p-3 text-center ${
                    p.available > 0
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                      : 'bg-slate-50 dark:bg-slate-800/50'
                  }`}>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Siap Dijual</p>
                    <p className={`text-3xl font-bold ${p.available > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                      {p.available || 0}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">dari {p.total} voucher total</p>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-50 dark:bg-slate-800 rounded p-2">
                      <p className="text-slate-500 text-[10px]">Terjual</p>
                      <p className="text-blue-600 font-bold">{p.sold || 0}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 rounded p-2">
                      <p className="text-slate-500 text-[10px]">Komisi/vc</p>
                      <p className="text-orange-600 font-bold">{fmtIDR(p.commission_per_voucher)}</p>
                    </div>
                  </div>

                  {/* Earned */}
                  {p.commission_earned > 0 && (
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                      <p className="text-[10px] text-slate-500">Komisi didapat</p>
                      <p className="text-sm font-bold text-emerald-600">{fmtIDR(p.commission_earned)}</p>
                    </div>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 text-xs"
                    onClick={(e) => { e.stopPropagation(); openPackage(p); }}
                    data-testid={`pkg-detail-${p.package_id}`}
                  >
                    Lihat Kode Voucher
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Recent Sold */}
      {recent_sold && recent_sold.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="py-3 pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Voucher Terbaru Terjual ({recent_sold.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {recent_sold.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-slate-900 dark:text-white text-xs">{r.code}</p>
                    <p className="text-[10px] text-slate-500">{r.package_name} • {r.used_at || '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{fmtIDR(r.price)}</p>
                    <p className="text-sm font-bold text-emerald-600">+{fmtIDR(r.commission_amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal preview voucher codes */}
      <Dialog open={!!pkgModal} onOpenChange={(o) => !o && setPkgModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              {pkgModal?.package_name}
              <span className="ml-auto text-sm font-normal text-slate-500">
                {pkgVouchers.length} voucher siap dijual
              </span>
            </DialogTitle>
          </DialogHeader>
          {pkgLoading ? (
            <div className="text-center py-10">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-amber-500" />
            </div>
          ) : pkgVouchers.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              Tidak ada voucher tersedia. Semua sudah terjual atau habis stock.
            </div>
          ) : (
            <>
              {/* View mode toggle */}
              <div className="flex gap-2 mb-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 transition ${viewMode === 'list' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  data-testid="tab-list"
                >
                  <List className="w-3.5 h-3.5" /> Format List
                </button>
                <button
                  onClick={() => setViewMode('qr')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 transition ${viewMode === 'qr' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  data-testid="tab-qr"
                >
                  <QrCode className="w-3.5 h-3.5" /> Format QR Card
                </button>
              </div>

              {viewMode === 'list' ? (
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Kode</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Password</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold">Harga</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold">Masa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pkgVouchers.map((v) => (
                        <tr key={v.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="px-3 py-1.5 font-mono text-xs">{v.code}</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{v.password || v.code}</td>
                          <td className="px-3 py-1.5 text-right text-xs">{fmtIDR(v.price)}</td>
                          <td className="px-3 py-1.5 text-xs">{fmtDur(v.validity_days, v.validity_unit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {pkgVouchers.slice(0, 24).map((v) => {
                      const qrText = window.location.origin + '/hs/redeem?u=' + encodeURIComponent(v.code) + '&p=' + encodeURIComponent(v.password || v.code);
                      return (
                        <div key={v.id} className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-2 text-center bg-white dark:bg-slate-900">
                          <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wide">{data?.agent?.branch_name || 'HOTSPOT'}</p>
                          <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{v.package_name}</p>
                          <div className="flex justify-center my-1">
                            <QRCodeSVG value={qrText} size={80} />
                          </div>
                          <p className="text-xs font-bold font-mono bg-amber-100 dark:bg-amber-900/30 text-slate-900 dark:text-amber-200 px-1 py-0.5 rounded">{v.code}</p>
                          <p className="text-[10px] font-mono text-slate-500 mt-0.5">{v.password || v.code}</p>
                          <p className="text-[10px] text-slate-500">{fmtDur(v.validity_days, v.validity_unit)}</p>
                          <p className="text-xs font-bold text-emerald-600 mt-1">{fmtIDR(v.price)}</p>
                        </div>
                      );
                    })}
                  </div>
                  {pkgVouchers.length > 24 && (
                    <p className="text-xs text-slate-500 text-center mt-3">
                      Preview 24 dari {pkgVouchers.length} voucher. Klik <b>Cetak QR</b> untuk print semua (4 per baris di A4).
                    </p>
                  )}
                </div>
              )}
              <div className="text-xs text-slate-500 mt-2">
                💡 {viewMode === 'list'
                  ? 'Klik Salin untuk copy kode ke clipboard, atau Cetak untuk print list.'
                  : 'QR mengandung kode + password. Customer scan → info otomatis muncul di kamera → tinggal login.'}
              </div>
            </>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPkgModal(null)}>Tutup</Button>
            {pkgVouchers.length > 0 && (
              <>
                <Button variant="outline" onClick={copyCodes} data-testid="copy-codes-btn">
                  <Copy className="w-4 h-4 mr-1" /> Salin
                </Button>
                {viewMode === 'list' ? (
                  <Button onClick={printCodes} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="print-codes-btn">
                    <Printer className="w-4 h-4 mr-1" /> Cetak
                  </Button>
                ) : (
                  <Button onClick={printQRCards} className="bg-purple-500 hover:bg-purple-600 text-white" data-testid="print-qr-btn">
                    <QrCode className="w-4 h-4 mr-1" /> Cetak QR Card
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <VoucherUsageCheckModal open={usageOpen} onOpenChange={setUsageOpen} initialCode="" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color, highlight, isText, testid }) {
  const colorMap = {
    slate: 'text-slate-700 dark:text-slate-200',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    blue: 'text-blue-600 dark:text-blue-400',
    orange: 'text-orange-600 dark:text-orange-400',
  };
  return (
    <Card className={`shadow-sm ${highlight ? 'ring-2 ring-emerald-400/40' : ''}`} data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs mb-1">
          <Icon className="w-3.5 h-3.5" />
          <span>{label}</span>
        </div>
        <p className={`${isText ? 'text-lg' : 'text-2xl'} font-bold ${colorMap[color] || 'text-slate-900'}`}>
          {value}
        </p>
        {subtext && <p className="text-[10px] text-slate-400 mt-0.5">{subtext}</p>}
      </CardContent>
    </Card>
  );
}
